import { useFormikContext } from "formik";
import { useCallback, useEffect, useState } from "react";
import { FormField } from "@/components/common/form-field";
import { ZipField } from "@/components/common/masked-form-field";
import { Textarea } from "@/components/ui/textarea";
import { onboardingCounties } from "@/features/portal/api";
import {
  CheckboxField,
  RadioField,
  SelectField,
} from "@/features/portal/components/onboarding/onboarding-fields";
import { STATE_OPTIONS } from "@/features/portal/components/onboarding/onboarding-options";
import {
  type TabProps,
  useBlurSave,
} from "@/features/portal/components/onboarding/tabs/tab-shared";
import type { OnboardingValues } from "@/features/portal/components/onboarding/wizard-schema";

const ADDRESS_TYPE_OPTIONS = [
  { value: "DOMESTIC", label: "Domestic" },
  { value: "INTERNATIONAL", label: "International" },
];

interface AddressBlockProps extends TabProps {
  /** Field-name prefix — "home" or "mailing". */
  prefix: "home" | "mailing";
  /** The onboarding token, for county lookups. */
  token: string;
}

/** One Domestic/International address block (used for both home and mailing). */
function AddressBlock({ prefix, token, save }: AddressBlockProps) {
  const { values } = useFormikContext<OnboardingValues>();
  const onBlur = useBlurSave(save);
  const typeField = `${prefix}_address_type` as keyof OnboardingValues;
  const stateField = `${prefix}_state` as keyof OnboardingValues;
  const type = values[typeField] as string;
  const state = values[stateField] as string;

  const [counties, setCounties] = useState<string[]>([]);
  const [loadingCounties, setLoadingCounties] = useState(false);

  const loadCounties = useCallback(
    async (st: string) => {
      if (!st) {
        setCounties([]);
        return;
      }
      setLoadingCounties(true);
      try {
        setCounties(await onboardingCounties(token, st));
      } catch {
        setCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    },
    [token],
  );

  // Populate the county list whenever the (domestic) state changes; passing ""
  // clears the list. Kept async so no state is set synchronously in the effect.
  useEffect(() => {
    void loadCounties(type === "DOMESTIC" ? state : "");
  }, [type, state, loadCounties]);

  const isDomestic = type === "DOMESTIC";

  return (
    <div className="grid gap-6">
      <RadioField
        name={typeField as string}
        label="Address Type"
        options={ADDRESS_TYPE_OPTIONS}
        onCommit={save}
      />

      {isDomestic ? (
        <>
          <FormField
            name={`${prefix}_street`}
            label="Street Address"
            onBlur={onBlur(`${prefix}_street` as keyof OnboardingValues)}
          />
          <FormField
            name={`${prefix}_line2`}
            label="Address Line 2 (Optional)"
            onBlur={onBlur(`${prefix}_line2` as keyof OnboardingValues)}
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField
              name={`${prefix}_city`}
              label="City"
              onBlur={onBlur(`${prefix}_city` as keyof OnboardingValues)}
            />
            <SelectField
              name={`${prefix}_state`}
              label="State"
              options={STATE_OPTIONS}
              placeholder="Select state"
              onCommit={(name, value) => {
                save(name, value);
                loadCounties(value);
              }}
            />
            <ZipField
              name={`${prefix}_zip`}
              label="ZIP Code"
              onBlur={onBlur(`${prefix}_zip` as keyof OnboardingValues)}
            />
            <SelectField
              name={`${prefix}_county`}
              label={loadingCounties ? "County (loading…)" : "County"}
              options={counties.map((c) => ({ value: c, label: c }))}
              placeholder={state ? "Select county" : "Select a state first"}
              onCommit={save}
            />
          </div>
          {prefix === "home" && (
            <FormField
              name="home_timezone"
              label="Time Zone (auto-derived)"
              readOnly
              disabled
              value={(values.home_timezone as string) || ""}
            />
          )}
        </>
      ) : (
        <>
          <div className="grid gap-2">
            <label
              htmlFor={`${prefix}_international_address`}
              className="text-sm font-medium"
            >
              International Address
            </label>
            <Textarea
              id={`${prefix}_international_address`}
              value={(values[`${prefix}_international_address` as keyof OnboardingValues] as string) || ""}
              onChange={(e) => save(`${prefix}_international_address`, e.target.value)}
              onBlur={onBlur(`${prefix}_international_address` as keyof OnboardingValues)}
              rows={3}
            />
          </div>
          <div className="sm:max-w-sm">
            <FormField
              name={`${prefix}_country`}
              label="Country"
              onBlur={onBlur(`${prefix}_country` as keyof OnboardingValues)}
            />
          </div>
        </>
      )}
    </div>
  );
}

/** Address Information tab — home block + optional mailing block. */
export function AddressTab({ save, token }: TabProps & { token: string }) {
  const { values } = useFormikContext<OnboardingValues>();
  return (
    <div className="grid gap-8">
      <section className="grid gap-4">
        <h3 className="font-heading text-foreground text-lg font-semibold">Home Address</h3>
        <AddressBlock prefix="home" token={token} save={save} />
      </section>

      <div className="border-t pt-4">
        <CheckboxField
          name="mailing_is_different"
          label="Mailing address is different from home address"
          onCommit={save}
        />
      </div>

      {values.mailing_is_different && (
        <section className="grid gap-4">
          <h3 className="font-heading text-foreground text-lg font-semibold">Mailing Address</h3>
          <AddressBlock prefix="mailing" token={token} save={save} />
        </section>
      )}
    </div>
  );
}
